import { initI18n } from "@fmc-digital/i18n";
import { InitializeFullStory } from "@fmc-digital/core-utils";

import { initGtm } from "../analytics/gtm";
import { getAuthConfig, getApiBaseUrl, loadConfig } from "../config/auth.config";
import type { RuntimeConfig } from "../config/auth.config";
import { applyVwoSharedConfigFromRuntime } from "../config/vwoSharedConfig";
import { setUnAuthTokenAndTimer } from "../services/UnAuthToken.service";
import type { AppInitResult, AuthShellConfig } from "./types";
import {
  initHttpClient,
  setOktaBaseUrl,
} from "@fmc-digital/core-auth";
declare global {
  interface Window {
    RUNTIME_CONFIG?: { translationsBaseUrl?: string };
    __authShellConfig?: AuthShellConfig;
  }
}

/**
 * Set RUNTIME_CONFIG on window for i18n translations CDN.
 * Must run before initI18n since it reads window.RUNTIME_CONFIG.
 */
function setRuntimeConfigOnWindow(runtimeConfig: RuntimeConfig): void {
  if (typeof window === "undefined") return;

  window.RUNTIME_CONFIG = {
    ...(window.RUNTIME_CONFIG || {}),
    ...(runtimeConfig.translationsBaseUrl && {
      translationsBaseUrl: runtimeConfig.translationsBaseUrl,
    }),
  };
}

/**
 * Set __authShellConfig on window for unauth API calls and token refresh.
 */
async function setAuthShellConfigOnWindow(
  runtimeConfig: RuntimeConfig,
  apiBaseUrl: string,
): Promise<void> {
  if (typeof window === "undefined") return;

  const { config: configModule } = await import("../config");
  const xApiKey = runtimeConfig.api.xApiKey ?? "";

  window.__authShellConfig = {
    apiBaseUrl,
    apiBaseUrlt1: runtimeConfig.api.baseUrlt1,
    baseurlv2: runtimeConfig.api.baseUrlV2, // <-- ensure baseurlv2 is set for Orval endpoints
    xApiKey,
    getConfigValue: (key: string) => configModule.env(key),
    refreshUnauthTokenIfNeeded: async () => {
      await setUnAuthTokenAndTimer(
        runtimeConfig.api.baseUrl,
        runtimeConfig.api.xApiKey ?? undefined,
      );
    },
  };
}

/**
 * Load unauthenticated token. Logs and continues on failure so app can still load.
 */
async function loadUnauthToken(runtimeConfig: RuntimeConfig): Promise<void> {
  try {
    await setUnAuthTokenAndTimer(
      runtimeConfig.api.baseUrl,
      runtimeConfig.api.xApiKey ?? undefined,
    );
  } catch (tokenError) {
    console.error("Failed to load unauthenticated token:", tokenError);
  }
}

/** Cached promise so multiple consumers (e.g. StrictMode double-mount) share the same init. */
let initPromise: Promise<AppInitResult | null> | null = null;

/**
 * Returns a promise that resolves when app init is done. Reuses in-flight or completed init.
 * On rejection, the cached promise is cleared so subsequent calls can retry.
 * Use this in hooks so that every mount (including React StrictMode's second mount) gets the result.
 */
export function getAppInitPromise(): Promise<AppInitResult | null> {
  if (!initPromise) {
    initPromise = initializeApp().catch((err) => {
      initPromise = null;
      throw err;
    });
  }
  return initPromise;
}

/** Reset cached init promise so the next getAppInitPromise() call will retry initialization. */
export function resetAppInitPromise(): void {
  initPromise = null;
}

/** Reset cached init promise (for tests only). Alias for resetAppInitPromise. */
export function resetAppInitPromiseForTesting(): void {
  resetAppInitPromise();
}

/**
 * Runs full app initialization: config, i18n, auth, HTTP client, window config, unAuth token.
 * Call once per app lifecycle (e.g. from useAppInitialization).
 */
export async function initializeApp(): Promise<AppInitResult | null> {
  const runtimeConfig = await loadConfig();
  // Inject Okta base URL into shared auth package
  setOktaBaseUrl(runtimeConfig.oktaUrl);
  const { config: configModule } = await import("../config");
  await configModule.init(runtimeConfig);
  applyVwoSharedConfigFromRuntime(runtimeConfig);

  // Initialize FullStory with org ID from environment config
  // Falls back to 'WYV65' if not specified in config
  try {
    const trimmedOrgId = runtimeConfig.fullStoryOrgId?.trim() ?? '';
    const fullStoryOrgId = trimmedOrgId || 'WYV65';
    
    console.log(`[App] Initializing FullStory with org ID: ${fullStoryOrgId}`);
    
    InitializeFullStory({ orgId: fullStoryOrgId });
    
    console.log(`[App] FullStory initialization call completed`);
  } catch (error) {
    console.error('[App] Failed to initialize FullStory:', error);
    // Log additional context to help debug
    console.error('[App] FullStory initialization error details:', {
      orgId: runtimeConfig.fullStoryOrgId,
      error: error instanceof Error ? error.message : String(error),
    });
  }

  try {
    initGtm(runtimeConfig.googleTagManagerCode);
    if (runtimeConfig.googleTagManagerCode?.trim()) {
      console.log(`[App] Google Tag Manager initialized: ${runtimeConfig.googleTagManagerCode.trim()}`);
    }
  } catch (error) {
    console.error("[App] Failed to initialize Google Tag Manager:", error);
  }

  setRuntimeConfigOnWindow(runtimeConfig);

  const i18nMode = runtimeConfig.translationsBaseUrl ? "auto" : "local";
  const i18nInstance = await initI18n({
    defaultLanguage: "en",
    fallbackLanguage: "en",
    mode: i18nMode,
  });

  const [authConfig, apiBaseUrl] = await Promise.all([
    getAuthConfig(runtimeConfig),
    getApiBaseUrl(runtimeConfig),
  ]);

  initHttpClient({ baseURL: apiBaseUrl });
  await setAuthShellConfigOnWindow(runtimeConfig, apiBaseUrl);
  await loadUnauthToken(runtimeConfig);

  return { authConfig, i18nInstance };
}
